import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import whatsappIcon from "../../assets/nopicture.png";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Avatar,
  Box,
  Chip,
  Menu,
  MenuItem,
  TableContainer,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ForbiddenPage from "../../components/ForbiddenPage";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  page: {
    gap: theme.spacing(3),
  },
  headerTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 28,
    fontWeight: 800,
    lineHeight: 1.15,
  },
  headerSubtitle: {
    marginTop: theme.spacing(0.75),
    color: "#64748b",
    fontSize: 14,
  },
  headerActions: {
    alignItems: "center",
    [theme.breakpoints.down("xs")]: {
      "& .MuiTextField-root": {
        width: "100%",
      },
    },
  },
  searchField: {
    minWidth: 280,
    "& .MuiOutlinedInput-root": {
      height: 42,
      borderRadius: 999,
      background: "#fff",
    },
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
  },
  mainPaper: {
    flex: 1,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    background: "#fff",
  },
  tableContainer: {
    maxHeight: "calc(100vh - 220px)",
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeadCell: {
    background: "#f8fafc",
    color: "#475569",
    fontWeight: 800,
    fontSize: 12,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e5e7eb",
  },
  tableRow: {
    height: 72,
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    color: "#1f2937",
    borderBottom: "1px solid #eef2f7",
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
  },
  userAvatar: {
    width: 42,
    height: 42,
    fontWeight: 700,
    background: "#e0f2fe",
    color: "#0369a1",
  },
  avatarDiv: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  nameCell: {
    fontWeight: 700,
    color: "#111827",
  },
  mutedText: {
    color: "#64748b",
  },
  queueStack: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    maxWidth: 220,
    margin: "0 auto",
  },
  queueChip: {
    height: 24,
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
  },
  profileChip: {
    height: 26,
    borderRadius: 999,
    fontWeight: 800,
    textTransform: "capitalize",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
  },
  iconButton: {
    width: 36,
    height: 36,
    color: "#475569",
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
    color: "#64748b",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
  },
  loadingText: {
    marginLeft: theme.spacing(2),
  },
}));

const Users = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = useState(null);
  const [users, dispatch] = useReducer(reducer, []);
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const companyId = loggedInUser.companyId;

  const [showInternalChat, setShowInternalChat] = useState(false);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      if (!companyId) {
        return;
      }

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);

        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

useEffect(() => {
  if (loggedInUser) {
    const companyId = loggedInUser.companyId;
    
    const onCompanyUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    };
    
    socket.on(`company-${companyId}-user`, onCompanyUser);
    
    return () => {
      socket.off(`company-${companyId}-user`, onCompanyUser);
    };
  }
}, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

const renderProfileImage = (user) => {
  // Para o usuário logado, verificar localStorage também
  if (user.id === loggedInUser.id) {
    const savedProfileImage = localStorage.getItem("profileImage");
    const profileImageToUse = savedProfileImage || user.profileImage;

    return (
      <Avatar
        src={profileImageToUse ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}` : undefined}
        alt={user.name}
        className={classes.userAvatar}
        onError={(e) => {
          e.target.src = whatsappIcon;
        }}
      >
        {user.name?.charAt(0)?.toUpperCase()}
      </Avatar>
    );
  }
  
  // Para outros usuários
  return (
    <Avatar
      src={user.profileImage ? `${backendUrl}/public/company${user.companyId}/user/${user.profileImage}` : undefined}
      alt={user.name}
      className={classes.userAvatar}
    >
      {user.name?.charAt(0)?.toUpperCase()}
    </Avatar>
  );
};

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success("Chat criado com sucesso!");
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    setAdminMenuAnchorEl(null);
    if (
      !window.confirm(
        "Tem certeza que deseja gerar chats para todos os usuários existentes? Isso pode demorar e criar muitos chats."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success("Chats gerados com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderQueues = (queues = []) => {
    if (!queues.length) {
      return <Typography variant="body2" className={classes.mutedText}>Sem filas</Typography>;
    }

    const visibleQueues = queues.slice(0, 2);
    const hiddenCount = queues.length - visibleQueues.length;

    return (
      <div className={classes.queueStack}>
        {visibleQueues.map((queue) => (
          <Chip
            key={queue.id}
            size="small"
            label={queue.name}
            className={classes.queueChip}
            style={{
              backgroundColor: queue.color || "#e0f2fe",
              color: "#0f172a",
            }}
          />
        ))}
        {hiddenCount > 0 && (
          <Tooltip title={queues.slice(2).map((queue) => queue.name).join(", ")}>
            <Chip size="small" label={`+${hiddenCount}`} className={classes.queueChip} />
          </Tooltip>
        )}
      </div>
    );
  };

  return (
    <MainContainer className={classes.page}>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${
            deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />
      {loggedInUser.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Box>
              <h1 className={classes.headerTitle}>Usuários</h1>
              <Typography className={classes.headerSubtitle}>
                Gerencie acessos, permissões e horários da equipe
              </Typography>
            </Box>
            <MainHeaderButtonsWrapper className={classes.headerActions}>
              <TextField
                className={classes.searchField}
                placeholder="Buscar por nome ou email"
                type="search"
                value={searchParam}
                onChange={handleSearch}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
              >
                {i18n.t("users.buttons.add")}
              </Button>
              {loggedInUser.profile === "admin" && showInternalChat && (
                <>
                <Tooltip title="Ações administrativas de uso eventual">
                  <span>
                    <Button
                      variant="outlined"
                      onClick={(event) => setAdminMenuAnchorEl(event.currentTarget)}
                      disabled={loading}
                      endIcon={<MoreVertIcon />}
                    >
                      Mais ações
                    </Button>
                  </span>
                </Tooltip>
                <Menu
                  anchorEl={adminMenuAnchorEl}
                  open={Boolean(adminMenuAnchorEl)}
                  onClose={() => setAdminMenuAnchorEl(null)}
                >
                  <MenuItem
                    onClick={handleBackfillChats}
                    title="Cria conversas internas para usuários existentes quando o chat interno está ativo."
                  >
                    <SettingsBackupRestoreIcon fontSize="small" style={{ marginRight: 8 }} />
                    Gerar Chats Existentes
                  </MenuItem>
                </Menu>
                </>
              )}
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper
            className={classes.mainPaper}
            variant="outlined"
          >
            <TableContainer className={classes.tableContainer} onScroll={handleScroll}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Status
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">Avatar</TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Nome
                    </TableCell>
                    <TableCell className={classes.tableHeadCell}>
                      Email
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Perfil
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Filas
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Conexão padrão
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Início de trabalho
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Fim de trabalho
                    </TableCell>
                    <TableCell className={classes.tableHeadCell} align="center">
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <>
                    {users.map((user) => (
                      <TableRow key={user.id} className={classes.tableRow}>
                        <TableCell className={classes.tableCell} align="center">
                          <Tooltip title={user.online ? "Online" : "Offline"}>
                            <span>
                              <UserStatusIcon user={user} />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          <div className={classes.avatarDiv}>
                            {renderProfileImage(user)}
                          </div>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography className={classes.nameCell}>{user.name}</Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <Typography variant="body2" className={classes.mutedText}>{user.email}</Typography>
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          <Chip
                            className={classes.profileChip}
                            label={user.profile}
                            size="small"
                            color={user.profile === "admin" ? "primary" : "default"}
                            variant={user.profile === "admin" ? "default" : "outlined"}
                          />
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          {renderQueues(user.queues)}
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          {user.whatsapp?.name || <span className={classes.mutedText}>Sem conexão</span>}
                        </TableCell>
                        <TableCell className={classes.tableCell} align="center">{user.startWork || "-"}</TableCell>
                        <TableCell className={classes.tableCell} align="center">{user.endWork || "-"}</TableCell>
                        <TableCell className={classes.tableCell} align="center">
                          <div className={classes.actionButtons}>
                            <Tooltip title="Criar chat interno">
                              <span>
                                <IconButton
                                  className={classes.iconButton}
                                  size="small"
                                  onClick={() => handleCreateChat(user)}
                                  disabled={!showInternalChat}
                                >
                                  <ChatBubbleOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Editar usuário">
                              <IconButton
                                className={classes.iconButton}
                                size="small"
                                onClick={() => handleEditUser(user)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir usuário">
                              <IconButton
                                className={classes.iconButton}
                                size="small"
                                onClick={() => {
                                  setConfirmModalOpen(true);
                                  setDeletingUser(user);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <div className={classes.emptyState}>
                            Nenhum usuário encontrado.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    {loadingMore && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                </TableBody>
              </Table>
            </TableContainer>
            {loading && !loadingMore && (
              <div className={classes.loadingContainer}>
                <CircularProgress />
                <span className={classes.loadingText}>{i18n.t("loading")}</span>
              </div>
            )}
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Users;
